// Finar 14.7 (K5) — Review Aksiyonlar panelinde bölüm bazlı "Yeniden üret".
// Onay penceresi tek bölüm tahminini gösterir (`GET …/generate/section/:code/estimate`);
// kirli editör / onaylı bölüm / süren üretim-KK durumunda düğme pasif + sebep satırı.
// Başlatınca `onStarted` → Review `loadSections()`; mevcut 3 sn yoklama ilerlemeyi gösterir.
import { useState } from "react";
import type { ReviewSection } from "@mahir/shared";
import { estimateSection, regenerateSection } from "../../api/projects";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { formatCost } from "../generation/GenerateConfirmModal";
import { REVIEW_COPY } from "../../lib/review-copy";

const C = REVIEW_COPY.regenerate;

interface Props {
  projectId: string;
  /** null = seçili bölüm yok → hiçbir şey render edilmez (Review.tsx satır bütçesi). */
  section: ReviewSection | null;
  hasUnsavedChanges: boolean;
  onStarted: () => void;
}

const isBusy = (v: string | null | undefined): boolean => v === "queued" || v === "running";
/** Üretim kuyrukta/sürüyor — Review editörü bu sürede salt-okunur (bitince yeni metin gelir). */
export const sectionBusy = (s: ReviewSection): boolean => isBusy(s.generationStatus);

/** Pasiflik sebebi (öncelik: onaylı → süren iş → KK adayı → kirli editör); null = etkin. */
export function regenerateDisabledReason(
  section: ReviewSection,
  hasUnsavedChanges: boolean,
): string | null {
  if (section.reviewStatus === "approved") return C.disabledApproved;
  if (isBusy(section.generationStatus) || isBusy(section.qcStatus)) return C.disabledRunning;
  if (section.qcStatus === "ready" && section.qcMeta?.promptType === "rewrite") {
    return C.disabledQcPending;
  }
  if (hasUnsavedChanges) return C.disabledDirty;
  return null;
}

export function RegenerateSectionButton({
  projectId,
  section,
  hasUnsavedChanges,
  onStarted,
}: Props): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [cost, setCost] = useState<string>(C.costLoading);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  if (!section) return null;
  const reason = regenerateDisabledReason(section, hasUnsavedChanges);

  const openDialog = (): void => {
    setOpen(true);
    setMessage(null);
    setCost(C.costLoading);
    estimateSection(projectId, section.sectionCode)
      .then((e) => setCost(formatCost(e.estimatedCostUsd)))
      .catch(() => setCost(C.costUnknown));
  };

  const confirm = async (): Promise<void> => {
    setBusy(true);
    try {
      await regenerateSection(projectId, section.sectionCode);
      setMessage({ ok: true, text: C.started });
      onStarted();
    } catch (e) {
      setMessage({ ok: false, text: e instanceof Error && e.message ? e.message : C.failed });
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="mb-2">
      <button
        type="button"
        data-testid="regenerate-section"
        onClick={openDialog}
        disabled={reason !== null || busy}
        title={reason ?? undefined}
        className="w-full px-3 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-primary)",
          color: "var(--color-primary)",
          fontWeight: 600,
        }}
      >
        ↻ {C.button}
      </button>
      {reason !== null && (
        <p
          className="mt-1 text-xs"
          data-testid="regenerate-section-reason"
          style={{ color: "var(--color-text-3)" }}
        >
          {reason}
        </p>
      )}
      {message && (
        <p
          className="mt-1 text-xs"
          role={message.ok ? "status" : "alert"}
          style={{ color: message.ok ? "var(--color-success)" : "var(--color-error)" }}
        >
          {message.text}
        </p>
      )}
      <ConfirmDialog
        open={open}
        title={C.title}
        body={C.body(cost)}
        confirmLabel={C.confirm}
        tone="neutral"
        busy={busy}
        onConfirm={() => void confirm()}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
}
